import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Users, Baby, ClipboardList, CalendarCheck, Receipt, Inbox } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { useFetch } from "../../lib/useFetch";
import { api, errMsg, fmtDate } from "../../lib/api";
import { StatusBadge } from "../../components/site/FileUpload";
import { AdminH } from "./AdminLayout";

const CapacityAlert = ({ a, reload }) => {
  const act = async (body) => {
    try {
      await api.put(`/admin/events/${a.event_id}`, body);
      toast.success("Event updated");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const [wl, setWl] = useState(null);
  const enableWl = () => setWl(a.waitlist_max || 5);
  return (
    <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-orange-200 bg-orange-50 p-5" data-testid={`capacity-alert-${a.event_id}`}>
      <div className="flex items-start gap-3">
        <AlertTriangle className="text-[#FF5722]" />
        <div>
          <p className="font-semibold text-[#0A2540]">{a.title}</p>
          <p className="text-sm text-slate-600">{a.remaining === 0 ? "Event is full" : `Only ${a.remaining} places left`} (max {a.max}).{a.waitlist_enabled ? ` Waiting list active: ${a.waitlist}/${a.waitlist_max}` : ""}</p>
        </div>
      </div>
      <div className="flex gap-2">
        <button onClick={() => act({ status: "Registrations closed" })} className="rounded-full bg-[#0A2540] px-4 py-2 text-sm font-semibold text-white" data-testid={`alert-close-${a.event_id}`}>Close registrations</button>
        <button onClick={enableWl} className="rounded-full border border-[#0A2540] px-4 py-2 text-sm font-semibold text-[#0A2540]" data-testid={`alert-waitlist-${a.event_id}`}>{a.waitlist_enabled ? "Edit waiting list" : "Accept waiting list"}</button>
      </div>
      <Dialog open={wl !== null} onOpenChange={(o) => !o && setWl(null)}>
        <DialogContent className="max-w-sm" data-testid="waitlist-dialog">
          <DialogHeader><DialogTitle>Waiting list – {a.title}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Maximum number of people on the waiting list</p>
          <Input type="number" min={1} value={wl ?? ""} onChange={(e) => setWl(e.target.value)} data-testid="waitlist-max-input" />
          <button onClick={() => { act({ waitlist_enabled: true, waitlist_max: Number(wl) }); setWl(null); }} disabled={!(Number(wl) > 0)} className="btn-buoy w-fit disabled:opacity-50" data-testid="waitlist-confirm">Enable waiting list</button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default function AdminDashboard() {
  const { data: d, reload } = useFetch("/admin/dashboard");
  if (!d) return null;
  const stats = [[Users, "Registered users", d.users, "users"], [Baby, "Minors", d.minors, "minors"], [ClipboardList, "Active registrations", d.registrations, "registrations"],
    [CalendarCheck, "Events open", d.events_open, "events-open"], [Receipt, "Receipts to verify", d.pending_receipts, "pending-receipts"], [Inbox, "New contact requests", d.contact_requests, "contact-requests"]];
  return (
    <div data-testid="admin-dashboard">
      <AdminH title="Dashboard" sub="Atlantis Academy organization overview" />
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-6">
        {stats.map(([Icon, l, v, id]) => (
          <div key={id} className="rounded-2xl border border-slate-200 bg-white p-5"><Icon size={18} className="text-[#00A8B5]" /><p className="mt-3 font-display text-3xl font-extrabold text-[#0A2540]" data-testid={`stat-${id}`}>{v}</p><p className="text-xs text-slate-500">{l}</p></div>
        ))}
      </div>
      {d.alerts.length > 0 && <div className="mt-8 space-y-3" data-testid="capacity-alerts"><p className="text-sm font-bold uppercase tracking-wider text-[#FF5722]">Capacity alerts</p>{d.alerts.map((a) => <CapacityAlert key={a.event_id} a={a} reload={reload} />)}</div>}
      <div className="mt-8 grid gap-6 xl:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 xl:col-span-2">
          <p className="mb-4 font-display text-lg font-bold text-[#0A2540]">Upcoming events</p>
          <div className="divide-y divide-slate-100">
            {d.upcoming.map((e) => (
              <Link key={e.id} to={`/admin/events/${e.id}`} className="flex items-center justify-between gap-4 py-3 hover:bg-slate-50">
                <div><p className="font-semibold text-[#0A2540]">{e.title}</p><p className="text-xs text-slate-500">{fmtDate(e.start_date)} · {(e.class_names || [e.class_name]).join(", ")}</p></div>
                <div className="flex items-center gap-3"><span className="text-sm font-semibold text-slate-600">{e.capacity.registered}/{e.capacity.max_participants || "∞"}</span><StatusBadge status={e.status} /></div>
              </Link>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="mb-4 flex items-center justify-between"><p className="font-display text-lg font-bold text-[#0A2540]">Notifications</p>
            {d.notifications.length > 0 && <button onClick={() => api.post("/admin/notifications/read").then(reload)} className="text-xs font-semibold text-[#00A8B5]" data-testid="notifications-mark-read">Mark all as read</button>}</div>
          {!d.notifications.length && <p className="text-sm text-slate-500">No notifications.</p>}
          <div className="space-y-3">{d.notifications.map((n) => <p key={n.id} className="rounded-xl bg-slate-50 p-3 text-sm text-slate-600">{n.message}<span className="block text-xs text-slate-400">{fmtDate(n.created_at)}</span></p>)}</div>
        </div>
      </div>
    </div>
  );
}

export function ContactRequests() {
  const { data } = useFetch("/admin/contact-requests");
  return (
    <div data-testid="admin-contact-requests">
      <AdminH title="Contact requests" sub="Messages from the public contact form" />
      <div className="space-y-3">
        {(data || []).map((c) => (
          <div key={c.id} className="rounded-2xl border border-slate-200 bg-white p-5">
            <p className="font-semibold text-[#0A2540]">{c.subject || "(no subject)"} <span className="font-normal text-slate-500">— {c.name} &lt;{c.email}&gt;</span></p>
            <p className="mt-2 whitespace-pre-line text-sm text-slate-600">{c.message}</p><p className="mt-2 text-xs text-slate-400">{fmtDate(c.created_at)}</p>
          </div>
        ))}
        {data && !data.length && <p className="text-slate-500">No requests yet.</p>}
      </div>
    </div>
  );
}
